import { PostAlertsResponse } from '@toggle/toggle';
import { useEffect } from 'react';

import { QUERY_KEY_EVENT_ALERTS } from '~/react-query-hooks/events/use-event-alerts/useEventAlerts';
import { queryClient } from '~/services/query-client/queryClient';
import { useAuthStore } from '~/stores/use-auth/store/authStore';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useNotifications } from '~/stores/use-notifications/useNotifications';
import { useChat } from '~/views/alfred/research/use-chat/useChat';
import { usePortfolio } from '~/views/settings/portfolio-view/use-portfolio/usePortfolio';

const faviconLinks = {
  default: '/app/static/images/brand/favicon.svg',
  notification: '/app/static/images/brand/favicon-notification.svg',
};

export const useNotificationsListener = () => {
  const token = useAuthStore(s => s.token);
  const isAlertsFlagEnabled = useFlags(state =>
    state.getFeatureFlagState('alerts')
  );
  const handlePortfolioWebsocketResponse = usePortfolio(
    state => state.handlePortfolioWebsocketResponse
  );

  const { subscribeToMessageOfType, unsubscribe } = useNotifications(state => ({
    subscribeToMessageOfType: state.subscribeToMessageOfType,
    unsubscribe: state.unsubscribe,
  }));
  const {
    getConversationStates,
    markConversationReadStatus,
    markTaskReceivedWsUpdate,
  } = useChat(state => ({
    getConversationStates: state.getConversationStates,
    markConversationReadStatus: state.markConversationReadStatus,
    markTaskReceivedWsUpdate: state.markTaskReceivedWsUpdate,
  }));

  const toggleFavicon = (key: keyof typeof faviconLinks) => {
    const faviconElement = document.querySelector("link[rel*='icon']");
    faviconElement?.setAttribute('href', faviconLinks[key]);
  };

  useEffect(() => {
    if (isAlertsFlagEnabled) {
      const refetchAlerts = async () => {
        const queryKey = [QUERY_KEY_EVENT_ALERTS, { count_only: true }];
        await queryClient.invalidateQueries({ queryKey, exact: true });
        const queryData = queryClient.getQueryData<{
          pages: PostAlertsResponse[];
        }>(queryKey);
        const unreadCount = queryData?.pages[0]?.unread_count;
        toggleFavicon(unreadCount ? 'notification' : 'default');
      };

      refetchAlerts();
    } else {
      return useChat.subscribe(state => {
        const completeUnreadConversationsCount =
          state.getCompleteUnreadConversations().length;

        toggleFavicon(
          completeUnreadConversationsCount ? 'notification' : 'default'
        );
      });
    }

    return undefined;
  }, [isAlertsFlagEnabled]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    if (isAlertsFlagEnabled) {
      subscribeToMessageOfType('alerts', async response => {
        if (response.event.action !== 'request.fail') {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_EVENT_ALERTS],
            exact: false,
          });
        }
      });
    } else {
      subscribeToMessageOfType('conversation', async response => {
        const { conversation_id: conversationId, request_id: requestId } =
          response.metadata;

        markTaskReceivedWsUpdate(conversationId, requestId);

        await getConversationStates({
          conversationId,
          taskId: requestId,
        });

        if (response.event.action !== 'request.fail') {
          markConversationReadStatus(
            conversationId,
            response.event.action === 'request.cancel'
          );
        }
      });
    }

    subscribeToMessageOfType('portfolio', async response => {
      handlePortfolioWebsocketResponse(response);
    });

    return unsubscribe;
  }, [token]);
};
